import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';
import {
  FormFieldBlueprint,
  createFormField,
  formFieldsApiRef,
} from '@backstage/plugin-scaffolder-react/alpha';
import {
  ApiBlueprint,
  appTreeApiRef,
  createExtensionInput,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';

// ── Shared types ──────────────────────────────────────────────────────────────

type FieldProps<T = string> = {
  onChange: (value: T) => void;
  rawErrors?: string[];
  required?: boolean;
  formData?: T;
  // title and description are the standard JSON Schema annotations the
  // scaffolder passes through from the template. They were absent here, which
  // is why every custom field rendered without a label or help text even though
  // the templates supply both.
  schema?: { default?: T; title?: string; description?: string };
  uiSchema?: { 'ui:options'?: Record<string, unknown> };
  formContext?: { formData?: Record<string, unknown> };
};

// ── SelectFieldFromApi ────────────────────────────────────────────────────────
// Single-select dropdown whose options are fetched from a Backstage backend API.
//
// ui:options:
//   path     (required) - API path under /api/, e.g. "silk-infra/hosts"
//   valueKey (required) - key in each response item used as the option value
//   labelKey           - display key (defaults to valueKey)
//   params             - static query params appended to the request

function SelectFieldFromApiComponent({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
}: FieldProps<string>) {
  const fetchApi = useApi(fetchApiRef);
  const opts = (uiSchema?.['ui:options'] ?? {}) as {
    path: string;
    valueKey: string;
    labelKey?: string;
    params?: Record<string, string>;
  };

  const [items, setItems] = useState<{ value: string; label: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const paramsKey = JSON.stringify(opts.params ?? {});

  useEffect(() => {
    if (!opts.path || !opts.valueKey) return;
    setLoading(true);
    setFetchError(null);

    const qs = opts.params
      ? `?${new URLSearchParams(opts.params).toString()}`
      : '';
    fetchApi
      .fetch(`/api/${opts.path}${qs}`)
      .then((r: Response) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      })
      .then((data: Record<string, string>[]) => {
        setItems(
          data.map(item => ({
            value: item[opts.valueKey],
            label: item[opts.labelKey ?? opts.valueKey],
          })),
        );
        setLoading(false);
      })
      .catch((e: Error) => {
        setFetchError(e.message);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.path, paramsKey]);

  if (loading)
    return <CircularProgress size={20} style={{ margin: '8px 0' }} />;

  return (
    <FormControl
      fullWidth
      error={Boolean(rawErrors?.length) || Boolean(fetchError)}
      required={required}
    >
      {/* shrink keeps the label above the value rather than floating over it,
          which is what the built-in Backstage fields do for a plain Select. */}
      {schema?.title && (
        <InputLabel shrink required={required}>
          {schema.title}
        </InputLabel>
      )}
      <Select
        value={formData ?? ''}
        displayEmpty
        onChange={e => onChange(e.target.value as string)}
      >
        <MenuItem value="" disabled>
          <em>Select…</em>
        </MenuItem>
        {items.map(item => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </Select>
      {/* The description explains which system this dropdown is choosing, which
          the option values alone do not: a host name says what a machine is
          called, not what role it plays in the operation. */}
      {schema?.description && (
        <FormHelperText>{schema.description}</FormHelperText>
      )}
      {fetchError && (
        <FormHelperText>Failed to load options: {fetchError}</FormHelperText>
      )}
      {rawErrors?.map((err, i) => (
        <FormHelperText key={i}>{err}</FormHelperText>
      ))}
    </FormControl>
  );
}

// ── SilkEchoDatabasePicker ────────────────────────────────────────────────────
// Cascading single-select: re-fetches from /api/silk-infra/databases whenever
// the sibling host field (named by ui:options.hostField) changes value.
//
// ui:options:
//   hostField - name of the host field on the same form page (default: sourceHost)

function SilkEchoDatabasePickerComponent({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
  formContext,
}: FieldProps<string>) {
  const fetchApi = useApi(fetchApiRef);
  const hostField =
    (uiSchema?.['ui:options'] as { hostField?: string } | undefined)
      ?.hostField ?? 'sourceHost';
  const hostValue =
    (formContext?.formData?.[hostField] as string | undefined) ?? '';

  const [items, setItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  useEffect(() => {
    if (!hostValue) {
      setItems([]);
      onChange('');
      return;
    }
    setLoading(true);
    setFetchError(null);
    fetchApi
      .fetch(
        `/api/silk-infra/databases?hostName=${encodeURIComponent(hostValue)}`,
      )
      .then((r: Response) => {
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      })
      .then((data: { id: string; name: string }[]) => {
        const names = data.map(d => d.name);
        setItems(names);
        if (names.length > 0) {
          const current = formData || schema?.default;
          if (current && names.includes(current)) {
            onChange(current); // select default or confirm existing value
          } else if (formData && !names.includes(formData)) {
            onChange(''); // clear if current selection is no longer valid
          }
        }
        setLoading(false);
      })
      .catch((e: Error) => {
        setFetchError(e.message);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hostValue]);

  if (!hostValue) {
    return (
      <FormControl fullWidth disabled required={required}>
        {schema?.title && (
          <InputLabel shrink required={required}>
            {schema.title}
          </InputLabel>
        )}
        <Select value="" displayEmpty>
          <MenuItem value="" disabled>
            <em>Select a source host first</em>
          </MenuItem>
        </Select>
        {schema?.description && (
          <FormHelperText>{schema.description}</FormHelperText>
        )}
      </FormControl>
    );
  }

  if (loading)
    return <CircularProgress size={20} style={{ margin: '8px 0' }} />;

  return (
    <FormControl
      fullWidth
      error={Boolean(rawErrors?.length) || Boolean(fetchError)}
      required={required}
    >
      {schema?.title && (
        <InputLabel shrink required={required}>
          {schema.title}
        </InputLabel>
      )}
      <Select
        value={formData ?? ''}
        displayEmpty
        onChange={e => onChange(e.target.value as string)}
      >
        <MenuItem value="" disabled>
          <em>Select…</em>
        </MenuItem>
        {items.map(name => (
          <MenuItem key={name} value={name}>
            {name}
          </MenuItem>
        ))}
      </Select>
      {schema?.description && (
        <FormHelperText>{schema.description}</FormHelperText>
      )}
      {fetchError && (
        <FormHelperText>Failed to load databases: {fetchError}</FormHelperText>
      )}
      {rawErrors?.map((err, i) => (
        <FormHelperText key={i}>{err}</FormHelperText>
      ))}
    </FormControl>
  );
}

// ── SilkEchoHostMatcher ───────────────────────────────────────────────────────
// Watches a sibling regex-filter field and displays the connected hosts that
// match, live as the user types. Debounced at 400ms.
//
// ui:options:
//   filterField - name of the sibling field holding the regex (default: hostFilter)
//
// Emits the matched host name array as its value so minItems validation can
// block submission when the regex matches nothing.

function SilkEchoHostMatcherComponent({
  onChange,
  rawErrors,
  uiSchema,
  formContext,
}: FieldProps<string[]>) {
  const fetchApi = useApi(fetchApiRef);
  const filterField =
    (uiSchema?.['ui:options'] as { filterField?: string } | undefined)
      ?.filterField ?? 'hostFilter';
  const filterValue =
    (formContext?.formData?.[filterField] as string | undefined) ?? '';

  const [hosts, setHosts] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (!filterValue) {
      setHosts([]);
      onChange([]);
      setErr(null);
    } else {
      setLoading(true);
      setErr(null);
      timer = setTimeout(() => {
        fetchApi
          .fetch(
            `/api/silk-infra/topology-hosts?filter=${encodeURIComponent(
              filterValue,
            )}`,
          )
          .then((r: Response) => {
            if (!r.ok)
              return r.json().then((d: { error?: string }) => {
                throw new Error(d.error ?? `${r.status}`);
              });
            return r.json();
          })
          .then((data: { name: string }[]) => {
            const names = data.map(h => h.name);
            setHosts(names);
            onChange(names);
            setLoading(false);
          })
          .catch((e: Error) => {
            setErr(e.message);
            setHosts([]);
            onChange([]);
            setLoading(false);
          });
      }, 400);
    }
    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  if (!filterValue) {
    return (
      <Typography variant="caption" color="textSecondary">
        Enter a filter pattern above to see matching hosts.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="textSecondary">
          Resolving…
        </Typography>
      </Box>
    );
  }

  const hasError = Boolean(err) || Boolean(rawErrors?.length);

  return (
    <FormControl fullWidth error={hasError}>
      {hosts.length > 0 ? (
        <Box>
          <Typography
            variant="caption"
            style={{ marginBottom: 6, display: 'block' }}
          >
            {hosts.length} host{hosts.length !== 1 ? 's' : ''} in scope:
          </Typography>
          <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
            {hosts.map(name => (
              <Chip key={name} label={name} size="small" />
            ))}
          </Box>
        </Box>
      ) : (
        <Typography variant="caption" color="error">
          No connected hosts matched this pattern.
        </Typography>
      )}
      {err && <FormHelperText>{err}</FormHelperText>}
      {rawErrors?.map((e, i) => (
        <FormHelperText key={i}>{e}</FormHelperText>
      ))}
    </FormControl>
  );
}

// ── SilkEchoResetPreviewer ────────────────────────────────────────────────────
// Watches a sibling filter field, debounces 600ms, calls
// GET /api/silk-infra/reset-plan and displays the full DFS deletion plan
// inline so the user sees exactly what will be removed before submitting.
//
// ui:options:
//   mode        - 'host' or 'environment'
//   filterField - sibling field name (default: hostFilter / targetHost)

function SilkEchoResetPreviewerComponent({
  uiSchema,
  formContext,
}: FieldProps<string>) {
  const fetchApi = useApi(fetchApiRef);
  const opts = (uiSchema?.['ui:options'] ?? {}) as {
    mode?: string;
    filterField?: string;
  };
  const mode = opts.mode ?? 'environment';
  const filterField =
    opts.filterField ?? (mode === 'host' ? 'targetHost' : 'hostFilter');
  const filterValue =
    (formContext?.formData?.[filterField] as string | undefined) ?? '';

  const [planText, setPlanText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (!filterValue) {
      setPlanText(null);
      setErr(null);
    } else {
      setLoading(true);
      setErr(null);
      const param =
        mode === 'host'
          ? `mode=host&target=${encodeURIComponent(filterValue)}`
          : `mode=environment&filter=${encodeURIComponent(filterValue)}`;
      timer = setTimeout(() => {
        fetchApi
          .fetch(`/api/silk-infra/reset-plan?${param}`)
          .then((r: Response) => {
            if (!r.ok)
              return r.json().then((d: { error?: string }) => {
                throw new Error(d.error ?? `${r.status}`);
              });
            return r.json();
          })
          .then((data: { planText: string }) => {
            setPlanText(data.planText);
            setLoading(false);
          })
          .catch((e: Error) => {
            setErr(e.message);
            setPlanText(null);
            setLoading(false);
          });
      }, 600);
    }
    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue]);

  if (!filterValue) {
    return (
      <Typography variant="caption" color="textSecondary">
        Enter a filter above to preview the deletion plan.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="textSecondary">
          Building plan…
        </Typography>
      </Box>
    );
  }

  return (
    <FormControl fullWidth error={Boolean(err)}>
      {planText && (
        <Box
          component="pre"
          style={{
            fontSize: '0.78rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 6,
            padding: '12px 14px',
            color: 'rgba(255,255,255,0.75)',
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          {planText}
        </Box>
      )}
      {err && <FormHelperText>{err}</FormHelperText>}
    </FormControl>
  );
}

// ── Form field instances (module-scope) ───────────────────────────────────────

const selectFieldFromApi = createFormField({
  name: 'SelectFieldFromApi',
  component: SelectFieldFromApiComponent as never,
});

const silkEchoDatabasePicker = createFormField({
  name: 'SilkEchoDatabasePicker',
  component: SilkEchoDatabasePickerComponent as never,
});

const silkEchoHostMatcher = createFormField({
  name: 'SilkEchoHostMatcher',
  component: SilkEchoHostMatcherComponent as never,
});

const silkEchoResetPreviewer = createFormField({
  name: 'SilkEchoResetPreviewer',
  component: SilkEchoResetPreviewerComponent as never,
});

// ── Override api:scaffolder/form-fields ───────────────────────────────────────
// Directly override the form-fields API to inject our custom fields rather than
// relying on FormFieldBlueprint module attachment (which is silently dropped when
// the module→plugin association fails in resolveAppNodeSpecs). The override still
// collects built-in blueprint-attached fields via inputs.formFields so standard
// pickers (RepoUrlPicker etc.) continue to work alongside our custom ones.

const customFormFieldsApi = ApiBlueprint.makeWithOverrides({
  name: 'form-fields',
  inputs: {
    formFields: createExtensionInput([
      FormFieldBlueprint.dataRefs.formFieldLoader,
    ]),
  },
  factory(originalFactory, { inputs }) {
    const formFieldLoaders = inputs.formFields.map(
      (e: any) =>
        e.get(
          FormFieldBlueprint.dataRefs.formFieldLoader,
        ) as () => Promise<unknown>,
    );
    return originalFactory(
      (defineParams: any) =>
        defineParams({
          api: formFieldsApiRef,
          deps: { appTreeApi: appTreeApiRef },
          factory: () => ({
            async loadFormFields() {
              const blueprintFields = await Promise.all(
                formFieldLoaders.map(loader => loader()),
              );
              return [
                ...blueprintFields,
                selectFieldFromApi,
                silkEchoDatabasePicker,
                silkEchoHostMatcher,
                silkEchoResetPreviewer,
              ];
            },
          }),
        }) as never,
    );
  },
});

export const scaffolderFieldsModule = createFrontendModule({
  pluginId: 'scaffolder',
  extensions: [customFormFieldsApi],
});
